import { useState } from "react";
import { modals } from "@mantine/modals";
import {
  Button,
  Center,
  Checkbox,
  Divider,
  Group,
  Paper,
  Radio,
  SimpleGrid,
  Stack,
  Text,
  ThemeIcon,
} from "@mantine/core";
import { IconId, IconTicket } from "@tabler/icons-react";
import { FEATURE_FLAGS } from "@congrs/schemas";
import { useGetBadgeTemplates } from "../../api/badges";
import { BadgeThumbnail } from "../Badges/BadgeCard";
import { useIsFeatureEnabled } from "../../api/featureFlags";

const MODAL_ID = "campaign-attachment-picker";

// Fixed box so rows stay aligned whatever each badge's aspect ratio is.
const THUMB_HEIGHT = 48;
const THUMB_BOX_WIDTH = 68;

const CATEGORIES = [
  {
    key: "badge",
    icon: IconId,
    color: "grape",
    title: "Event ticket",
    subtitle: "Personalized badge or ticket PDF for each recipient",
  },
];

// The classic PDF ticket predates badge templates; it stays selectable
// alongside them as a built-in option, and needs no badges feature flag.
const TICKET_PDF_OPTION = {
  value: "eventTicket",
  icon: IconTicket,
  color: "red",
  name: "Ticket PDF",
  subtitle: "Classic PDF ticket with QR code",
};

const AttachmentPickerModalContent = ({
  initialAttachments,
  onConfirm,
  eventId,
}) => {
  const badgesEnabled = useIsFeatureEnabled(eventId, FEATURE_FLAGS.BADGES);
  const { data: badges } = useGetBadgeTemplates({ eventId });
  const [selected, setSelected] = useState(initialAttachments);

  const selectedBadgeId =
    selected.find((a) => a.type === "badge")?.badgeId ?? null;
  const ticketPdfSelected = selected.some((a) => a.type === "eventTicket");
  const badgeSelected =
    (badgesEnabled && selectedBadgeId !== null) || ticketPdfSelected;

  const options = [
    TICKET_PDF_OPTION,
    ...(badgesEnabled
      ? (badges ?? []).map((badge) => ({
          value: badge.id,
          badge,
          name: badge.name,
          subtitle: "Personalized badge for each recipient",
        }))
      : []),
  ];

  const selectedOption = ticketPdfSelected ? "eventTicket" : selectedBadgeId;

  const isCategoryChecked = (key) => (key === "badge" ? badgeSelected : false);

  const toggleCategory = (key, checked) => {
    if (key !== "badge") return;

    if (checked) {
      const firstBadge = badgesEnabled ? (badges ?? [])[0] : null;
      selectOption(firstBadge ? firstBadge.id : TICKET_PDF_OPTION.value);
    } else {
      setSelected((current) =>
        current.filter((a) => a.type !== "badge" && a.type !== "eventTicket")
      );
    }
  };

  const selectOption = (value) => {
    setSelected((current) => [
      ...current.filter((a) => a.type !== "badge" && a.type !== "eventTicket"),
      value === "eventTicket"
        ? { type: "eventTicket" }
        : { type: "badge", badgeId: value },
    ]);
  };

  const handleDone = () => {
    onConfirm(selected);
    modals.close(MODAL_ID);
  };

  const showBadgeOptions = badgeSelected;

  return (
    <Stack gap="lg">
      <Stack gap="sm">
        <Text size="sm" fw={500} c="dimmed">
          Attachments
        </Text>
        <SimpleGrid cols={2} spacing="md">
          {CATEGORIES.map(({ key, icon: Icon, color, title, subtitle }) => {
            const checked = isCategoryChecked(key);
            return (
              <Paper
                key={key}
                withBorder
                p="md"
                radius="md"
                onClick={() => toggleCategory(key, !checked)}
                style={{ cursor: "pointer" }}
              >
                <Group justify="space-between" wrap="nowrap" align="flex-start">
                  <Group gap="md" wrap="nowrap">
                    <ThemeIcon
                      variant="light"
                      color={color}
                      size="xl"
                      radius="md"
                    >
                      <Icon size={22} />
                    </ThemeIcon>
                    <div>
                      <Text fw={600}>{title}</Text>
                      <Text size="sm" c="dimmed">
                        {subtitle}
                      </Text>
                    </div>
                  </Group>
                  <Checkbox
                    checked={checked}
                    onChange={(e) =>
                      toggleCategory(key, e.currentTarget.checked)
                    }
                    onClick={(e) => e.stopPropagation()}
                    aria-label={title}
                  />
                </Group>
              </Paper>
            );
          })}
        </SimpleGrid>
      </Stack>

      {showBadgeOptions && (
        <>
          <Divider label="Choose one" labelPosition="center" />
          <SimpleGrid cols={2} spacing="md">
            {options.map(
              ({ value, icon: Icon, color, name, subtitle, badge }) => (
                <Paper
                  key={value}
                  withBorder
                  p="md"
                  radius="md"
                  onClick={() => selectOption(value)}
                  style={{ cursor: "pointer" }}
                >
                  <Group
                    justify="space-between"
                    wrap="nowrap"
                    align="flex-start"
                  >
                    <Group gap="md" wrap="nowrap">
                      {badge ? (
                        <Center w={THUMB_BOX_WIDTH} style={{ flexShrink: 0 }}>
                          <BadgeThumbnail
                            templateData={badge.templateData}
                            backgroundUrl={badge.backgroundKey}
                            height={THUMB_HEIGHT}
                          />
                        </Center>
                      ) : (
                        <ThemeIcon
                          variant="light"
                          color={color}
                          size="xl"
                          radius="md"
                        >
                          <Icon size={22} />
                        </ThemeIcon>
                      )}
                      <div style={{ minWidth: 0 }}>
                        <Text fw={600} lineClamp={1}>
                          {name}
                        </Text>
                        <Text size="sm" c="dimmed">
                          {subtitle}
                        </Text>
                      </div>
                    </Group>
                    <Radio
                      checked={selectedOption === value}
                      onChange={() => selectOption(value)}
                      onClick={(e) => e.stopPropagation()}
                      aria-label={name}
                    />
                  </Group>
                </Paper>
              )
            )}
          </SimpleGrid>
        </>
      )}

      <Group justify="flex-end">
        <Button variant="default" onClick={() => modals.close(MODAL_ID)}>
          Cancel
        </Button>
        <Button onClick={handleDone}>Done</Button>
      </Group>
    </Stack>
  );
};

export const openAttachmentPickerModal = ({
  initialAttachments,
  onConfirm,
  eventId,
}) => {
  modals.open({
    modalId: MODAL_ID,
    title: "Add attachments",
    size: "lg",
    children: (
      <AttachmentPickerModalContent
        initialAttachments={initialAttachments}
        onConfirm={onConfirm}
        eventId={eventId}
      />
    ),
  });
};
